import { signOut } from 'aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import dayjs from 'dayjs';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import ErrorDialog from '@/components/shared/errorDialog';
import GroupIcon from '@/components/shared/image/icon_group.svg';
import { MobiAppBarAction } from '@/components/shared/MobiAppBarAction';
import { MobiTitle } from '@/components/shared/MobiTitle';
import StorageKeys from '@/constants/storageKeys';
import { apiGet } from '@/modules/hooks/use-query';
import { useServiceType } from '@/modules/hooks/useServiceType';
import * as paths from '@/routes/paths';
import { settingState } from '@/state/settingState';
import { ServiceType } from '@/types/serviceType';

type ErrorMsg = {
  message: string;
};
type Req = {
  driverID: string;
  dateFrom?: string;
  dateTo?: string;
};

type GroupedDriveListInfo = {
  driveDate: string;
  driveInfoList: DriveListInfoType[];
};

// 画面項目
type DriveListInfoType = {
  operationZoneName: string;
  directionName: string;
  vehicleOperationScheduleName: string;
  reservationCount: number;
  totalPassengerCount: number;
  totalAmount: number;
  driveDate: string;
  firstEstimatedPickupDatetime: string;
  firstPickupWaypointNumber: string;
  firstPickupWaypoint: string;
  lastEstimatedDropoffDatetime: string;
  lastDropoffWaypointNumber: string;
  lastDropoffWaypoint: string;
  vehicleOperationScheduleId: string;
  vehicleId: string;
  areaId: number;
  areaName: string;
};

type DriveListProps = {
  mockDriveList?: DriveListInfoType[];
};

const DriveList: React.FC<DriveListProps> = ({ mockDriveList }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [setting, setSetting] = useRecoilState(settingState);
  const driverID = setting?.driverId || '';
  const [driveListInfo, setDriveListInfo] = useState<GroupedDriveListInfo[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorOPen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const serviceType = useServiceType();
  const vehicleId = sessionStorage.getItem(StorageKeys.VEHICLE_ID);
  const showDriveDetails = (vehicleOperationScheduleId: string) => {
    sessionStorage.setItem(StorageKeys.VEHICLE_OPERATION_SCHEDULE_ID, vehicleOperationScheduleId);
    sessionStorage.setItem(StorageKeys.VEHICLE_ID, vehicleId?.toString() || '');
    navigate(paths.driveDetails);
  };

  const formatWaypointName = (number: string, waypoint: string) => {
    if (number === '自宅') {
      return `${number}.${waypoint}`;
    }
    if (waypoint.length > 10) {
      return `${waypoint.substring(0, 10)}…`;
    }

    return waypoint;
  };

  /** "HH:mm" または "HH:mm:ss" を分に変換（0:00からの経過分）。パース失敗時は null */
  const timeStringToMinutes = (timeStr: string): number | null => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const parts = timeStr.trim().split(':').map(Number);
    if (parts.length < 2 || parts.some(Number.isNaN)) return null;
    const [h, m] = parts;

    return h * 60 + m;
  };

  const groupDriveListInfo = useCallback((data: DriveListInfoType[]): GroupedDriveListInfo[] => {
    const driveInfoMap = data.reduce((groupData, item) => {
      const driveDate = item?.driveDate || '';
      if (!groupData.has(driveDate)) {
        groupData.set(driveDate, []);
      }
      groupData.get(driveDate)?.push(item);

      return groupData;
    }, new Map<string, DriveListInfoType[]>());

    return Array.from(driveInfoMap, ([driveDate, driveListInfoType]) => ({
      driveDate,
      driveInfoList: driveListInfoType.sort(
        (a: DriveListInfoType, b: DriveListInfoType) =>
          new Date(a.firstEstimatedPickupDatetime).getTime() - new Date(b.firstEstimatedPickupDatetime).getTime(),
      ),
    })).sort(
      (a: GroupedDriveListInfo, b: GroupedDriveListInfo) =>
        new Date(a.driveDate).getTime() - new Date(b.driveDate).getTime(),
    );
  }, []);

  const getClosestFutureItem = (items: DriveListInfoType[]): DriveListInfoType | null => {
    if (items.length === 0) {
      return null;
    }

    const now = new Date();
    const nowTime = now.getTime();

    return items
      .map((item) => {
        const [hours, minutes] = item.firstEstimatedPickupDatetime.split(':').map(Number);
        const itemTime = dayjs(`${item.driveDate} ${hours}:${minutes}`).toDate().getTime();

        return { item, itemTime };
      })
      .filter(({ itemTime }) => itemTime > nowTime)
      .sort((a, b) => a.itemTime - b.itemTime)[0]?.item;
  };

  useEffect(() => {
    if (mockDriveList) {
      const groupDriveListMap = groupDriveListInfo(mockDriveList);
      setDriveListInfo(groupDriveListMap);
      setIsLoaded(true);
      setErrorOpen(false);

      return;
    }

    const getGata = async (req: Req) => {
      try {
        apiGet('driverApi', `/v1/drives`, {
          queryParams: req,
        })
          .then((res) => {
            const data = res as DriveListInfoType[];
            if (data[0]) {
              const groupDriveListmap = groupDriveListInfo(data);
              setDriveListInfo(groupDriveListmap);
              setErrorOpen(false);
            } else {
              setDriveListInfo([]);
            }
            setIsLoaded(true);
          })
          .catch((error) => {
            const err = error as ErrorMsg;
            setErrorMessage(I18n.get(err.message) as string);
            setErrorOpen(true);
          });
      } catch (error) {
        const err = error as ErrorMsg;
        setErrorMessage(I18n.get(err.message) as string);
        setErrorOpen(true);
      }
    };

    let req: Req = { driverID };
    if (serviceType === ServiceType.PreBooking) {
      req = {
        ...req,
        dateFrom: dayjs().format('YYYY-MM-DD'),
        dateTo: dayjs().add(1, 'day').format('YYYY-MM-DD'),
      };
    } else if (serviceType === ServiceType.FixedRouteSchedule || serviceType === ServiceType.RealtimeOnDemand) {
      req = {
        ...req,
      };
    } else {
      signOut()
        .then(() => {
          setSetting(undefined);
          navigate(paths.login);
        })
        .catch(() => {});
    }
    if (vehicleId && driverID !== null) {
      void getGata(req);
    }

    const intervalId = setInterval(() => {
      if (vehicleId) {
        if (driverID !== null) {
          void getGata(req);
        }
      }
    }, 120000);

    return () => clearInterval(intervalId);
  }, [driverID, serviceType, navigate, setSetting, vehicleId, mockDriveList, groupDriveListInfo]);

  const onHandleClick = () => {
    // ブラウザ上にドライバーマニュアルを表示する。
    const { VITE_DRIVER_MANUAL } = import.meta.env;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    window.open(VITE_DRIVER_MANUAL, '_blank');
  };

  return (
    <div className="page-root drive-list-page">
      <div className="page-shell">
        <div className="layout-body">
          <div className="primary-column">
            <MobiTitle>ドライブ一覧</MobiTitle>
            <MobiAppBarAction>
              <button
                type="button"
                className="icon-action-button"
                onClick={() => navigate(paths.vehicleDriverUpdate, { state: { vehicleId } })}
                aria-label="車両確認へ移動"
              >
                <span className="icon-action-mark" aria-hidden>
                  ≡
                </span>
              </button>
            </MobiAppBarAction>

            {driveListInfo.length > 0 ? (
              (() => {
                const allItems = driveListInfo.flatMap((g) => g.driveInfoList);
                const nextDepartureItem = getClosestFutureItem(allItems);

                return driveListInfo.map((group, groupIndex) => (
                  <section
                    className={`date-section ${groupIndex === driveListInfo.length - 1 ? 'is-last' : ''}`}
                    key={group.driveDate}
                  >
                    <h2 className="date-heading">
                      {group.driveDate ? `${dayjs(group.driveDate).format('MM月DD日')}${t('driveList.driveDate')}` : ''}
                    </h2>
                    {group.driveInfoList && group.driveInfoList.length > 0
                      ? (() =>
                          group.driveInfoList
                            .sort((a, b) =>
                              a.firstEstimatedPickupDatetime.localeCompare(b.firstEstimatedPickupDatetime),
                            )
                            .map((item) => (
                              <button
                                type="button"
                                className={`trip-card ${
                                  nextDepartureItem?.vehicleOperationScheduleId === item.vehicleOperationScheduleId
                                    ? 'is-active'
                                    : ''
                                }`}
                                key={`${item.vehicleOperationScheduleId}-${item.firstEstimatedPickupDatetime}`}
                                onClick={() => showDriveDetails(item.vehicleOperationScheduleId)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    showDriveDetails(item.vehicleOperationScheduleId);
                                  }
                                }}
                              >
                                <div className="trip-main">
                                  <div className="route-block">
                                    <div className="route-segment">
                                      <div className="route-row route-row-single-line">
                                        <span className="route-time">{item.firstEstimatedPickupDatetime}</span>
                                        {item.firstPickupWaypointNumber !== '自宅' ? (
                                          <span className="waypoint-tag is-pickup">
                                            {item.firstPickupWaypointNumber}
                                          </span>
                                        ) : null}
                                        <span className="route-location">
                                          {formatWaypointName(item.firstPickupWaypointNumber, item.firstPickupWaypoint)}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="route-segment">
                                      <div className="route-row route-row-single-line">
                                        <span className="route-time">{item.lastEstimatedDropoffDatetime}</span>
                                        {item.lastDropoffWaypointNumber !== '自宅' ? (
                                          <span className="waypoint-tag is-dropoff">
                                            {item.lastDropoffWaypointNumber}
                                          </span>
                                        ) : null}
                                        <span className="route-location">
                                          {formatWaypointName(item.lastDropoffWaypointNumber, item.lastDropoffWaypoint)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="trip-meta-row">
                                    <div className="trip-summary">
                                      <div className="summary-item">
                                        <img src={GroupIcon} className="summary-icon" alt="" />
                                        <span className="summary-text">
                                          {item.reservationCount}組 {item.totalPassengerCount}名
                                        </span>
                                      </div>
                                      <div className="summary-item">
                                        <span className="summary-icon">¥</span>
                                        <span className="summary-text">{item.totalAmount}円</span>
                                      </div>
                                      <span className="trip-duration">
                                        {(() => {
                                          const startMin = timeStringToMinutes(item.firstEstimatedPickupDatetime);
                                          const endMin = timeStringToMinutes(item.lastEstimatedDropoffDatetime);
                                          if (startMin === null || endMin === null) return null;
                                          let diff = endMin - startMin;
                                          if (diff < 0) diff += 24 * 60;

                                          return (
                                            <>
                                              <AccessTimeIcon sx={{ fontSize: 16, color: '#64748b', mr: 0.5 }} />
                                              {`${diff}分`}
                                            </>
                                          );
                                        })()}
                                      </span>
                                    </div>
                                    <div className="trip-meta-row-sub">
                                      <div className="route-name">
                                        <span>{item.operationZoneName}</span>
                                        <span>{item.directionName}</span>
                                        <span>{item.vehicleOperationScheduleName}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <span className="navigate-button" aria-hidden="true">
                                  <svg
                                    width="10"
                                    height="18"
                                    viewBox="0 0 10 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <title>navigate</title>
                                    <path
                                      d="M1 1L9 9L1 17"
                                      stroke="#94a3b8"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </span>
                              </button>
                            )))()
                      : ''}
                  </section>
                ));
              })()
            ) : isLoaded ? (
              <div className="empty-state-card">
                <p className="empty-state-text">{t('driveList.noData')}</p>
              </div>
            ) : null}
            <div className="manual-link-row">
              <button className="manual-link-button" type="button" onClick={() => onHandleClick()}>
                {t('driveList.showContent')}
              </button>
            </div>
            {(serviceType === ServiceType.FixedRouteSchedule || serviceType === ServiceType.RealtimeOnDemand) &&
            !vehicleId ? (
              <p className="notice-text">{t('driveList.areaFujiedaMsg')}</p>
            ) : null}
            {serviceType === ServiceType.PreBooking && !vehicleId ? (
              <p className="notice-text">{t('driveList.areaDigidenMsg')}</p>
            ) : null}
            {errorOPen ? (
              <ErrorDialog open={errorOPen} errorMessage={errorMessage} onClose={(flag) => setErrorOpen(flag)} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriveList;
